import { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useTableColumns } from './useTableColumns';
import { useTableActions } from './useTableActions';
import { RowActionsMenu } from '../RowActionsMenu';
import { ReportShipData } from '@ais-ui/websockets';

export interface UseTableColumnsWithActionsProps {
  shipData: ReportShipData[] | undefined;
}

/**
 * Custom hook that combines table columns with actions
 * This separates the concerns and avoids circular dependencies
 */
export const useTableColumnsWithActions = ({
  shipData,
}: UseTableColumnsWithActionsProps) => {
  // Get base columns and filters
  const { columns: baseColumns, filters } = useTableColumns({
    shipData,
  });

  // Get actions
  const { actions, hasActions } = useTableActions({
    shipData,
  });

  // Combine columns with actions
  const columnsWithActions = useMemo(() => {
    if (!hasActions) {
      return baseColumns;
    }

    const actionsColumn: ColumnDef<any, any> = {
      id: 'actions',
      header: '',
      cell: ({ row }: any) => (
        <RowActionsMenu row={row.original} actions={actions} />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 50,
    };

    return [...baseColumns, actionsColumn];
  }, [baseColumns, actions, hasActions]);

  return {
    columns: columnsWithActions,
    filters,
    actions,
    hasActions,
    baseColumns,
  };
};
